import React from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box, Button, Divider, Grid, IconButton, Tooltip,
  useTheme, useMediaQuery, TextField, InputAdornment
} from "@mui/material";
import { ClearAllOutlined, FileDownloadOutlined, FilterAltOutlined, Search } from "@mui/icons-material";

import { BASE_CONSTANTS } from "constants/baseConstants";
import FilterMine from "./FilterMine";
import ParamsKeywordSearch from "components/Shared/ParamsKeywordSearch";
import { BaseDrawer } from "components/Shared/Base/BaseDrawer";
import { ActionCard } from "components/Shared/UI/ActionCard/ActionCard";


const TableFilter = ({
  children,
  filterMineKey = null,
  debounceDelay = 750,
  minLength = 4,
  searchLabel = "",
  searchPlaceholder = "Search...",
  initialFilters = 2,
  disabled = false,
  exportButton = null,
  filterButton = null,
  actions = null,
  startContent = null,
  stripHyphen = false,
  // OLD PROPS - for backward compatibility
  exportComponent: ExportComponent = null,
  onClear = () => { },
}) => {
  const [showFilters, setShowFilters] = React.useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileKeyword, setMobileKeyword] = React.useState(searchParams.get("search") || "");
  const mobileTimerRef = React.useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));


  const handleSearchChange = (val) => {
    const newParams = new URLSearchParams(searchParams);
    const cleanVal = stripHyphen && val ? val.replace(/-/g, "").trim() : val;
    if (cleanVal) {
      newParams.set("search", cleanVal);
    } else {
      newParams.delete("search");
    }
    newParams.set("page", 1);
    setSearchParams(newParams);
  };

  const handleMobileSearchChange = (val) => {
    const cleanVal = stripHyphen ? val.replace(/-/g, "") : val;
    setMobileKeyword(cleanVal);
    clearTimeout(mobileTimerRef.current);
    if (!cleanVal) { handleSearchChange(""); return; }
    if (cleanVal.length < minLength) return;
    mobileTimerRef.current = setTimeout(() => handleSearchChange(cleanVal), debounceDelay);
  };

  const _filters = React.Children.toArray(children)
    .filter((child) => React.isValidElement(child))
    .reduce((acc, el) => {
      if (el.type !== React.Fragment) return [...acc, el];
      return [
        ...acc,
        ...el.props.children.filter((child) => React.isValidElement(child)),
      ];
    }, []);

  const _requiresDrawer = _filters?.length > initialFilters;
  const _primaryFilters = _requiresDrawer ? [..._filters].slice(0, initialFilters) : _filters;

  if (!_filters?.length && !searchLabel) return null;

  // Handle backward compatibility - prefer new props over old
  const renderExport = () => {
    if (actions) return <Box>{actions}</Box>;
    if (ExportComponent) {
      return (
        <IconButton disabled={disabled}>
          <ExportComponent />
        </IconButton>
      );
    }
    if (exportButton?.onClick) {
      return (
        <IconButton color="success" onClick={exportButton.onClick}>
          <FileDownloadOutlined />
        </IconButton>
      );
    }
    return null;
  };

  const handleClearAll = () => {
    setSearchParams({});
    setMobileKeyword("");
    if (onClear) onClear();
  };

  return (
    <>
      <Box sx={{ px: isMobile ? 2 : 4, py: isMobile ? 2 : 2 }}>
        {isMobile ? (
          <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
              {!!searchLabel && (
                <TextField
                  fullWidth
                  size="small"
                  placeholder={searchPlaceholder}
                  value={mobileKeyword}
                  onChange={(e) => handleMobileSearchChange(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      bgcolor: "background.paper",
                      boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
                      height: "45px"
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: "primary.main" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {_requiresDrawer && (
                <IconButton
                  onClick={() => setShowFilters(true)}
                  sx={{
                    height: 45, width: 45, minWidth: 45,
                    bgcolor: "primary.main", color: "white",
                    borderRadius: "12px",
                    "&:hover": { bgcolor: "primary.dark" }
                  }}
                >
                  <FilterAltOutlined />
                </IconButton>
              )}
            </Box>

            {(!!filterMineKey || exportButton || actions || ExportComponent) && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {!!filterMineKey && <FilterMine filterMineKey={filterMineKey} disabled={disabled} />}
                </Box>
                {renderExport()}
              </Box>
            )}
          </Box>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-4">
            {!!filterMineKey && <FilterMine filterMineKey={filterMineKey} disabled={disabled} />}
            {startContent}
            <div className="mr-auto flex flex-grow flex-wrap items-center gap-4">
              {_primaryFilters.map((item, index) => (
                <div key={index} className={item.props?.wrapperClassName || "w-60"}>
                  {React.cloneElement(item, { disabled, wrapperClassName: undefined })}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3">
              {!!_filters?.length && (
                <Tooltip title="Clear Filters">
                  <IconButton onClick={handleClearAll} disabled={disabled}>
                    <ClearAllOutlined color={disabled ? "disabled" : "action"} />
                  </IconButton>
                </Tooltip>
              )}
              {!!searchLabel && (
                <ParamsKeywordSearch
                  label={searchLabel}
                  placeholder={searchPlaceholder}
                  debounceDelay={debounceDelay}
                  minLength={minLength}
                  disabled={disabled}
                  stripHyphen={stripHyphen}
                />
              )}
              {_requiresDrawer && (
                <IconButton onClick={() => setShowFilters(true)}>
                  <FilterAltOutlined color="primary" />
                </IconButton>
              )}
              {renderExport()}
            </div>
          </div>
        )}
      </Box>

      <Divider />

      <BaseDrawer width={400} open={showFilters} onClose={() => setShowFilters(false)}>
        <BaseDrawer.Header onClose={() => setShowFilters(false)}>Filters</BaseDrawer.Header>
        <ActionCard>
          <ActionCard.Content>
            <Grid container spacing={BASE_CONSTANTS.SPACING_MD}>
              {_filters.map((item, index) => (
                <Grid key={index} item xs={12}>
                  {React.cloneElement(item, { fullWidth: true, disabled })}
                </Grid>
              ))}
              {filterButton?.onClick && (
                <Grid item xs={12}>
                  <Button fullWidth variant="contained" onClick={filterButton.onClick}>
                    Apply Filters
                  </Button>
                </Grid>
              )}
            </Grid>
          </ActionCard.Content>
        </ActionCard>
        <ActionCard.Actions>
          <Button fullWidth onClick={handleClearAll}>Clear All</Button>
        </ActionCard.Actions>
      </BaseDrawer>
    </>
  );
};

export default TableFilter;